'use client'
import { useState, useEffect } from 'react';

const CheckoutLoadingOverlay = ({ isVisible, onComplete, type = 'checkout' }) => {
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  const [hasCompleted, setHasCompleted] = useState(false);
  
  const stepConfigs = {
    checkout: [
      { text: 'Memproses data booking...', duration: 800 },
      { text: 'Menyiapkan halaman checkout...', duration: 600 },
      { text: 'Mengenkripsi data...', duration: 400 },
      { text: 'Mengarahkan ke checkout...', duration: 300 }
    ],
    upsell: [
      { text: 'Memproses data booking...', duration: 500 },
      { text: 'Menyiapkan layanan tambahan...', duration: 400 },
      { text: 'Mengenkripsi data...', duration: 300 },
      { text: 'Mengarahkan ke halaman upsell...', duration: 300 }
    ]
  };
  
  const steps = stepConfigs[type] || stepConfigs.checkout;

  useEffect(() => {
    if (!isVisible) {
      setProgress(0);
      setCurrentStep(0);
      setHasCompleted(false);
      return;
    }

    if (hasCompleted) return;

    let isCancelled = false;
    let timeoutIds = [];
    
    // Calculate total duration
    const totalDuration = steps.reduce((sum, step) => sum + step.duration, 0);
    
    console.log(`🔄 Starting ${type} loading animation (${totalDuration}ms)`);

    // Simple step progression
    let currentTime = 0;
    steps.forEach((step, index) => {
      const stepTimeoutId = setTimeout(() => {
        if (!isCancelled && isVisible) {
          setCurrentStep(index);
          console.log(`Step ${index + 1}: ${step.text}`);
        }
      }, currentTime);
      timeoutIds.push(stepTimeoutId);
      currentTime += step.duration;
    });

    // Progress animation
    const startTime = Date.now();
    const animateProgress = () => {
      if (isCancelled || !isVisible) return;
      
      const elapsed = Date.now() - startTime;
      const progressPercent = Math.min((elapsed / totalDuration) * 100, 100);
      setProgress(progressPercent);
      
      if (progressPercent < 100) {
        requestAnimationFrame(animateProgress);
      }
    };
    animateProgress();

    // Complete loading
    const completeTimeoutId = setTimeout(() => {
      if (!isCancelled && isVisible && !hasCompleted) {
        console.log('✅ Loading completed');
        setProgress(100);
        setHasCompleted(true);
        
        if (onComplete) {
          console.log('🚀 Auto-skipping loading at 100% - navigating to checkout now');
          onComplete();
        } else {
          console.warn('⚠️ onComplete callback is not provided');
        }
      }
    }, totalDuration);
    timeoutIds.push(completeTimeoutId); 

    // Emergency fallback to prevent infinite loading
    const emergencyTimeoutId = setTimeout(() => {
      if (!isCancelled && isVisible && !hasCompleted) {
        console.warn('⚠️ Emergency timeout - forcing completion and navigation');
        setProgress(100);
        setHasCompleted(true);
        if (onComplete) {
          console.log('🚨 Emergency navigation to checkout');
          onComplete();
        }
      }
    }, totalDuration + 2000); // 2 seconds after normal completion
    timeoutIds.push(emergencyTimeoutId);

    // Cleanup function
    return () => {
      isCancelled = true;
      timeoutIds.forEach(id => clearTimeout(id));
    };
  }, [isVisible, hasCompleted]);

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 mx-auto mb-4 relative">
            {/* Animated checkout icon */}
            <div className="absolute inset-0 border-4 border-blue-200 rounded-full"></div>
            <div 
              className="absolute inset-0 border-4 border-blue-600 rounded-full border-t-transparent animate-spin"
              style={{ animationDuration: '1s' }}
            ></div>
            <div className="absolute inset-2 flex items-center justify-center">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13l2.5 5m6-5v6a2 2 0 01-2 2H9a2 2 0 01-2-2v-6m8 0V9a2 2 0 00-2-2H9a2 2 0 00-2 2v4.01" />
              </svg>
            </div>
          </div>
          <h3 className="text-xl font-semibold text-gray-800 mb-2">
            {type === 'checkout' ? 'Memproses Checkout' : 'Memproses Layanan Tambahan'}
          </h3>
          <p className="text-gray-600 text-sm">
            Mohon tunggu sebentar...
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-6">
          <div className="flex justify-between text-xs text-gray-500 mb-2">
            <span>Progress</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
            <div 
              className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full transition-all duration-300 ease-out"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>

        {/* Current Step */}
        <div className="space-y-3">
          {steps.map((step, index) => (
            <div 
              key={index}
              className={`flex items-center space-x-3 transition-all duration-300 ${
                index === currentStep ? 'text-blue-600' : 
                index < currentStep ? 'text-green-600' : 'text-gray-400'
              }`}
            >
              <div className={`w-4 h-4 rounded-full flex items-center justify-center text-xs ${
                index === currentStep ? 'bg-blue-600 text-white animate-pulse' :
                index < currentStep ? 'bg-green-600 text-white' : 'bg-gray-300'
              }`}>
                {index < currentStep ? (
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <span>{index + 1}</span>
                )}
              </div>
              <span className={`text-sm ${
                index === currentStep ? 'font-medium' : ''
              }`}>
                {step.text}
              </span>
              {index === currentStep && (
                <div className="flex space-x-1">
                  <div className="w-1 h-1 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-1 h-1 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-1 h-1 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500 mb-3">
            Jangan tutup atau refresh halaman ini
          </p>
          
          {/* Auto-skip message when near completion */}
          {progress > 90 && (
            <div className="text-xs text-green-600 font-medium animate-pulse">
              Auto-navigating to checkout...
            </div>
          )}
          
          {/* Skip button for manual skip */}
          {progress > 30 && progress < 90 && (
            <button
              onClick={() => {
                console.log('🔄 User clicked skip button');
                if (onComplete) {
                  onComplete();
                }
              }}
              className="text-xs text-blue-600 hover:text-blue-800 underline"
            >
              Skip loading
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CheckoutLoadingOverlay;
